import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Loader2, Package, Languages, Search, Copy, CheckCircle2 } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { Button } from '../components/ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
function getToken() {
  return localStorage.getItem('ervapos_token') || localStorage.getItem('token') || '';
}
function authHeaders() {
  return { Authorization: 'Bearer ' + getToken(), 'Content-Type': 'application/json' };
}

const AVISOS_PT = 'Armazenar em local fresco (abaixo de 25\u00b0C), seco e escuro. N\u00e3o exceda a dose di\u00e1ria expressamente recomendada. Manter fora do alcance de crian\u00e7as mais novas. Os suplementos alimentares n\u00e3o devem ser utilizados como substitutos de uma dieta equilibrada. \u00c9 necess\u00e1rio manter uma dieta variada e equilibrada e um estilo de vida saud\u00e1vel. Consulte o seu t\u00e9cnico de sa\u00fade se estiver gr\u00e1vida, a amamentar, se sofre de alguma doen\u00e7a ou toma medicamentos.';
const AVISOS_EN = 'Store in a cool (below 25\u00b0C), dry and dark place. Do not exceed the recommended daily dose. Keep out of reach of young children. Food supplements should not be used as substitutes for a balanced diet. A varied and balanced diet and a healthy lifestyle must be maintained. Consult your healthcare professional if you are pregnant, breastfeeding, suffering from any illness or taking medication.';
const EMPRESA_DEFAULT = 'Produzido e desenvolvido por Harrison SN Spain / Produced and developed by Harrison SN Spain. Distribu\u00eddo por / Distributed by FamaSa\u00fade Unipessoal Lda., Pra\u00e7a Dona Maria II 168, V. N. Famalic\u00e3o - Portugal. Lote e Validade / Batch & Expiry: Ver gravado na embalagem / See embossed on packaging';
const GMP_DEFAULT = 'PT: Produto fabricado em Laborat\u00f3rio Certificado GMP (Boas Pr\u00e1ticas de Fabrico). | EN: Product manufactured in a GMP Certified Laboratory (Good Manufacturing Practices).';

const EMPTY = {
  id: '', nome_comercial: '', subtitulo: '', tipo: 'capsulas',
  num_doses: '', tamanho_dose: '', toma_diaria: '',
  beneficios: ['', '', '', ''],
  nutrientes: [{ nome: '', qty: '', nrv: '', sub: false }],
  ingredientes_pt: '', ingredientes_en: '',
  modo_uso_pt: '', modo_uso_en: '',
  avisos_pt: AVISOS_PT, avisos_en: AVISOS_EN,
  empresa_bilingue: EMPRESA_DEFAULT,
  gmp_bilingue: GMP_DEFAULT,
  num_unidades: '', num_unidades_en: '',
  volume: '', volume_en: '',
  peso_liquido: '',
  rotulo_largura_mm: 180, rotulo_altura_mm: 74,
  nota_extra: '',
  info_qr_url: '',
  codigo_artigo_pos: '', ativo: true,
};

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-600">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, placeholder, type }) {
  return (
    <input type={type || 'text'} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
  );
}

function Textarea({ value, onChange, rows, placeholder }) {
  return (
    <textarea rows={rows || 3} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y" />
  );
}

// Tradução automática via Claude API
async function traduzirTexto(texto, contexto) {
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'anthropic-version': '2023-06-01' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: 'Traduz o seguinte texto de Portugues para Ingles. Responde APENAS com a traducao, sem explicacoes nem aspas. Contexto: rotulo de suplemento alimentar.\n\nTexto:\n' + texto
        }]
      })
    });
    const data = await res.json();
    if (data.content && data.content[0]) return data.content[0].text.trim();
    return '';
  } catch(e) {
    console.error('Erro traducao:', e);
    return '';
  }
}

export default function RotulosEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState({});
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [claims, setClaims] = useState([]);
  const [claimsMeta, setClaimsMeta] = useState({ nutrientes: [], areas: [] });
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimsSearch, setClaimsSearch] = useState('');
  const [claimsNutriente, setClaimsNutriente] = useState('');
  const [claimsArea, setClaimsArea] = useState('');
  const [claimsMsg, setClaimsMsg] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    fetch(API_BASE + '/labels/produtos/' + id, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data.detail) { setErro(data.detail); setLoading(false); return; }
        if (!Array.isArray(data.beneficios)) data.beneficios = [];
        while (data.beneficios.length < 4) data.beneficios.push('');
        if (!Array.isArray(data.nutrientes)) data.nutrientes = [];
        if (data.nutrientes.length === 0) data.nutrientes = [{ nome: '', qty: '', nrv: '', sub: false }];
        data.nutrientes = data.nutrientes.map(function(n) {
          return { nome: n.nome || '', qty: n.qty || '', nrv: n.nrv || '', sub: n.sub || false };
        });
        setForm(data);
        setLoading(false);
      })
      .catch(() => { setErro('Erro ao carregar produto.'); setLoading(false); });
  }, [id]);

  useEffect(() => {
    fetch(API_BASE + '/labels/claims-library/meta', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (!data.detail) setClaimsMeta({
          nutrientes: Array.isArray(data.nutrientes) ? data.nutrientes : [],
          areas: Array.isArray(data.areas) ? data.areas : [],
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setClaimsLoading(true);
      const params = new URLSearchParams();
      if (claimsSearch.trim()) params.set('search', claimsSearch.trim());
      if (claimsNutriente) params.set('nutriente', claimsNutriente);
      if (claimsArea) params.set('area', claimsArea);
      params.set('limit', '100');

      fetch(API_BASE + '/labels/claims-library?' + params.toString(), { headers: authHeaders() })
        .then(r => r.json())
        .then(data => {
          if (Array.isArray(data)) setClaims(data);
          else setClaims([]);
        })
        .catch(() => setClaims([]))
        .finally(() => setClaimsLoading(false));
    }, 250);

    return () => clearTimeout(timer);
  }, [claimsSearch, claimsNutriente, claimsArea]);

  const set = (field, value) => setForm(f => Object.assign({}, f, { [field]: value }));
  const setBen = (i, v) => { const b = [...form.beneficios]; b[i] = v; set('beneficios', b); };
  const addBen = () => set('beneficios', [...form.beneficios, '']);
  const delBen = (i) => set('beneficios', form.beneficios.filter((_, j) => j !== i));
  const setNut = (i, field, value) => {
    const n = form.nutrientes.map((r, j) => j === i ? Object.assign({}, r, { [field]: value }) : r);
    set('nutrientes', n);
  };
  const addNut = () => set('nutrientes', [...form.nutrientes, { nome: '', qty: '', nrv: '', sub: false }]);
  const delNut = (i) => set('nutrientes', form.nutrientes.filter((_, j) => j !== i));

  const addClaimToBenefits = (claimText) => {
    const txt = (claimText || '').trim();
    if (!txt) return;

    const current = Array.isArray(form.beneficios) ? [...form.beneficios] : [];
    if (current.some(b => (b || '').trim() === txt)) {
      setClaimsMsg('Esta alegação já está nos benefícios.');
      setTimeout(() => setClaimsMsg(''), 1800);
      return;
    }

    const emptyIndex = current.findIndex(b => !(b || '').trim());
    if (emptyIndex >= 0) current[emptyIndex] = txt;
    else current.push(txt);

    set('beneficios', current);
    setClaimsMsg('Alegação adicionada aos benefícios.');
    setTimeout(() => setClaimsMsg(''), 1800);
  };

  const applyClaimAsNote = (claimText) => {
    const txt = (claimText || '').trim();
    if (!txt) return;
    set('nota_extra', txt);
    setClaimsMsg('Alegação colocada na Nota Extra.');
    setTimeout(() => setClaimsMsg(''), 1800);
  };

  const copyClaim = async (claimText) => {
    const txt = (claimText || '').trim();
    if (!txt) return;
    try {
      await navigator.clipboard.writeText(txt);
      setClaimsMsg('Alegação copiada.');
    } catch(e) {
      setClaimsMsg('Não consegui copiar automaticamente.');
    }
    setTimeout(() => setClaimsMsg(''), 1800);
  };

  const handleTranslate = async (campo_pt, campo_en) => {
    const texto = form[campo_pt];
    if (!texto || !texto.trim()) return;
    setTranslating(t => Object.assign({}, t, { [campo_en]: true }));
    const traducao = await traduzirTexto(texto);
    if (traducao) set(campo_en, traducao);
    setTranslating(t => Object.assign({}, t, { [campo_en]: false }));
  };

  const handleSave = async () => {
    setErro(''); setSucesso(''); setSaving(true);
    const payload = Object.assign({}, form);
    payload.beneficios = payload.beneficios.filter(function(b) { return b.trim(); });
    try {
      const url = isEdit ? API_BASE + '/labels/produtos/' + id : API_BASE + '/labels/produtos';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro ao guardar');
      setSucesso(isEdit ? 'Produto actualizado!' : 'Produto criado!');
      setTimeout(() => navigate('/rotulos'), 1500);
    } catch(e) {
      setErro(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-20">
      <div className="border-b border-slate-800 bg-slate-900/60 px-8 py-4 flex items-center gap-4 sticky top-0 z-10">
        <Button variant="ghost" size="icon" onClick={() => navigate('/rotulos')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center">
            <Package className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-base font-bold">{isEdit ? 'Editar Produto' : 'Novo Produto'}</h1>
            <p className="text-xs text-slate-400">Rotulo Elemensys</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar
        </Button>
      </div>

      {erro && <div className="mx-8 mt-4 px-4 py-3 bg-rose-950/50 border border-rose-800 rounded-xl text-rose-300 text-sm">{erro}</div>}
      {sucesso && <div className="mx-8 mt-4 px-4 py-3 bg-emerald-950/50 border border-emerald-800 rounded-xl text-emerald-300 text-sm">{sucesso}</div>}

      <div className="max-w-4xl mx-auto px-8 py-6 space-y-8">

        {/* Identificacao */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Identificacao</h2>
          <div className="grid grid-cols-2 gap-4">
            {!isEdit && <Field label="ID do produto" hint="ex: omega3-1000-120caps"><Input value={form.id} onChange={v => set('id', v)} placeholder="omega3-1000-120caps" /></Field>}
            <Field label="Nome comercial"><Input value={form.nome_comercial} onChange={v => set('nome_comercial', v)} placeholder="Omega 3" /></Field>
            <Field label="Subtitulo"><Input value={form.subtitulo} onChange={v => set('subtitulo', v)} placeholder="Premium" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Tipo">
              <select value={form.tipo} onChange={e => set('tipo', e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                <option value="capsulas">Capsulas / Comprimidos / Pearls</option>
                <option value="liquido">Liquido / Xarope</option>
              </select>
            </Field>
            <Field label="Cod. artigo POS"><Input value={form.codigo_artigo_pos} onChange={v => set('codigo_artigo_pos', v)} placeholder="opcional" /></Field>
            <Field label="Ativo">
              <div className="flex items-center gap-2 h-10">
                <input type="checkbox" checked={form.ativo} onChange={e => set('ativo', e.target.checked)} className="w-4 h-4 accent-emerald-500" />
                <span className="text-sm text-slate-300">Produto activo</span>
              </div>
            </Field>
          </div>
        </section>

        {/* Unidades e Peso */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Unidades e Peso</h2>
          <div className="grid grid-cols-3 gap-4">
            {form.tipo === 'liquido' ? (
              <>
                <Field label="Volume (PT)"><Input value={form.volume} onChange={v => set('volume', v)} placeholder="250 ml" /></Field>
                <Field label="Volume (EN)"><Input value={form.volume_en} onChange={v => set('volume_en', v)} placeholder="250 ml" /></Field>
              </>
            ) : (
              <>
                <Field label="Unidades (PT)"><Input value={form.num_unidades} onChange={v => set('num_unidades', v)} placeholder="120 Capsulas Vegetais" /></Field>
                <Field label="Unidades (EN)"><Input value={form.num_unidades_en} onChange={v => set('num_unidades_en', v)} placeholder="120 Vegetable Capsules" /></Field>
              </>
            )}
            <Field label="Peso liquido"><Input value={form.peso_liquido} onChange={v => set('peso_liquido', v)} placeholder="113 g / 3,99 oz" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Largura rotulo (mm)"><Input type="number" value={form.rotulo_largura_mm} onChange={v => set('rotulo_largura_mm', parseInt(v) || 180)} /></Field>
            <Field label="Altura rotulo (mm)"><Input type="number" value={form.rotulo_altura_mm} onChange={v => set('rotulo_altura_mm', parseInt(v) || 74)} /></Field>
          </div>
        </section>

        {/* Beneficios */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Beneficios (PT — aparecem no rotulo em PT)</h2>
          <div className="space-y-2">
            {form.beneficios.map(function(b, i) {
              return (
                <div key={i} className="flex gap-2">
                  <Input value={b} onChange={v => setBen(i, v)} placeholder={'Beneficio ' + (i+1)} />
                  <Button variant="ghost" size="icon" onClick={() => delBen(i)} className="text-rose-400 hover:text-rose-300 flex-shrink-0"><Trash2 className="w-4 h-4" /></Button>
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addBen} className="border-slate-700 text-slate-400 hover:text-white">
              <Plus className="w-4 h-4 mr-1" /> Adicionar beneficio
            </Button>
          </div>
        </section>

        {/* Biblioteca de Alegações */}
        <section className="space-y-4 rounded-2xl border border-emerald-900/50 bg-emerald-950/10 p-4">
          <div className="flex items-start justify-between gap-4 border-b border-emerald-900/40 pb-3">
            <div>
              <h2 className="text-sm font-bold text-emerald-300 uppercase tracking-widest">Biblioteca de Alegações Autorizadas</h2>
              <p className="text-xs text-slate-500 mt-1">
                Pesquisa alegações por nutriente, área ou texto. Podes adicionar aos benefícios ou usar como Nota Extra.
              </p>
            </div>
            {claimsMsg && (
              <div className="flex items-center gap-1 text-xs text-emerald-300 bg-emerald-950/70 border border-emerald-800 rounded-lg px-2 py-1">
                <CheckCircle2 className="w-3 h-3" /> {claimsMsg}
              </div>
            )}
          </div>

          <div className="grid grid-cols-[1fr_180px_180px] gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
              <input
                value={claimsSearch}
                onChange={e => setClaimsSearch(e.target.value)}
                placeholder="Pesquisar: imunidade, energia, magnésio, vitamina C..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <select
              value={claimsNutriente}
              onChange={e => setClaimsNutriente(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Todos os nutrientes</option>
              {claimsMeta.nutrientes.map(n => <option key={n} value={n}>{n}</option>)}
            </select>

            <select
              value={claimsArea}
              onChange={e => setClaimsArea(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Todas as áreas</option>
              {claimsMeta.areas.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className="rounded-xl border border-slate-800 overflow-hidden">
            <div className="grid grid-cols-[130px_130px_1fr_250px] gap-3 bg-slate-900/80 px-3 py-2 text-xs text-slate-500 uppercase tracking-wider">
              <span>Nutriente</span>
              <span>Área</span>
              <span>Alegação</span>
              <span className="text-right">Acções</span>
            </div>

            <div className="max-h-80 overflow-y-auto divide-y divide-slate-800 bg-slate-950/60">
              {claimsLoading ? (
                <div className="flex items-center gap-2 px-3 py-4 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> A carregar alegações...
                </div>
              ) : claims.length === 0 ? (
                <div className="px-3 py-4 text-sm text-slate-500">
                  Nenhuma alegação encontrada. Experimenta pesquisar por “imunidade”, “energia”, “magnésio”, “zinco”, “vitamina C”...
                </div>
              ) : (
                claims.map(c => (
                  <div key={c.id} className="grid grid-cols-[130px_130px_1fr_250px] gap-3 px-3 py-3 items-start hover:bg-slate-900/60">
                    <div className="text-xs text-emerald-300 font-semibold">{c.nutriente}</div>
                    <div className="text-xs text-slate-400">{c.area}</div>
                    <div>
                      <div className="text-sm text-slate-100 leading-relaxed">{c.claim_pt}</div>
                      {c.condicao_uso && <div className="text-xs text-amber-300/80 mt-1">Condição: {c.condicao_uso}</div>}
                      {c.fonte_legal && <div className="text-xs text-slate-600 mt-1">Fonte: {c.fonte_legal}</div>}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addClaimToBenefits(c.claim_pt)}
                        className="border-slate-700 text-slate-300 hover:text-white"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Benefício
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => applyClaimAsNote(c.claim_pt)}
                        className="border-slate-700 text-slate-300 hover:text-white"
                      >
                        Nota
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => copyClaim(c.claim_pt)}
                        className="text-slate-500 hover:text-white h-8 w-8"
                        title="Copiar alegação"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Tabela Nutricional */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Tabela Nutricional</h2>
          <div className="grid grid-cols-3 gap-4">
            <Field label="N. doses / Servicos" hint="ex: 120"><Input value={form.num_doses || ''} onChange={v => set('num_doses', v)} placeholder="120" /></Field>
            <Field label="Tamanho da dose" hint="ex: 1 Perola / 1 Capsula"><Input value={form.tamanho_dose || ''} onChange={v => set('tamanho_dose', v)} placeholder="1 Capsula vegetal" /></Field>
            <Field label="Toma diaria recomendada" hint="ex: 1 Perola / dia"><Input value={form.toma_diaria || ''} onChange={v => set('toma_diaria', v)} placeholder="1 Capsula por dia" /></Field>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_130px_100px_50px_36px] gap-2 px-1">
              <span className="text-xs text-slate-500">Nutriente (PT/EN)</span>
              <span className="text-xs text-slate-500">Qty por dose</span>
              <span className="text-xs text-slate-500">NRV / VRN %</span>
              <span className="text-xs text-slate-500 text-center">Sub</span>
              <span></span>
            </div>
            {form.nutrientes.map(function(n, i) {
              return (
                <div key={i} className="grid grid-cols-[1fr_130px_100px_50px_36px] gap-2 items-center">
                  <Input value={n.nome} onChange={v => setNut(i, 'nome', v)} placeholder="Oleo de peixe (trigliceridos)" />
                  <Input value={n.qty} onChange={v => setNut(i, 'qty', v)} placeholder="1000 mg" />
                  <Input value={n.nrv || ''} onChange={v => setNut(i, 'nrv', v)} placeholder="++ ou 28%" />
                  <div className="flex items-center justify-center">
                    <input type="checkbox" checked={n.sub || false} onChange={e => setNut(i, 'sub', e.target.checked)}
                      className="w-4 h-4 accent-emerald-500" title="Linha subordinada (indentada)" />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => delNut(i)} className="text-rose-400 hover:text-rose-300 h-8 w-8"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addNut} className="border-slate-700 text-slate-400 hover:text-white">
              <Plus className="w-4 h-4 mr-1" /> Adicionar nutriente
            </Button>
            <p className="text-xs text-slate-600 mt-1">NRV: use "++" para valores sem referencia, ou percentagem ex: "28%"</p>
          </div>
        </section>

        {/* Ingredientes */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Ingredientes</h2>
          <Field label="Ingredientes PT">
            <Textarea rows={4} value={form.ingredientes_pt} onChange={v => set('ingredientes_pt', v)} />
          </Field>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => handleTranslate('ingredientes_pt', 'ingredientes_en')}
              disabled={translating['ingredientes_en']} className="border-slate-700 text-slate-400 hover:text-white">
              {translating['ingredientes_en'] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Languages className="w-3 h-3 mr-1" />}
              Traduzir PT para EN
            </Button>
          </div>
          <Field label="Ingredientes EN">
            <Textarea rows={4} value={form.ingredientes_en} onChange={v => set('ingredientes_en', v)} />
          </Field>
        </section>

        {/* Modo de uso */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Modo de Utilizacao</h2>
          <Field label="Modo de uso PT"><Textarea rows={2} value={form.modo_uso_pt} onChange={v => set('modo_uso_pt', v)} /></Field>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => handleTranslate('modo_uso_pt', 'modo_uso_en')}
              disabled={translating['modo_uso_en']} className="border-slate-700 text-slate-400 hover:text-white">
              {translating['modo_uso_en'] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Languages className="w-3 h-3 mr-1" />}
              Traduzir PT para EN
            </Button>
          </div>
          <Field label="Modo de uso EN"><Textarea rows={2} value={form.modo_uso_en} onChange={v => set('modo_uso_en', v)} /></Field>
        </section>

        {/* Avisos */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Avisos</h2>
          <Field label="Avisos PT"><Textarea rows={3} value={form.avisos_pt} onChange={v => set('avisos_pt', v)} /></Field>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => handleTranslate('avisos_pt', 'avisos_en')}
              disabled={translating['avisos_en']} className="border-slate-700 text-slate-400 hover:text-white">
              {translating['avisos_en'] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Languages className="w-3 h-3 mr-1" />}
              Traduzir PT para EN
            </Button>
          </div>
          <Field label="Avisos EN"><Textarea rows={3} value={form.avisos_en} onChange={v => set('avisos_en', v)} /></Field>
        </section>


        {/* Nota Extra */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Nota / Alegacao Extra (coluna D do rotulo)</h2>
          <p className="text-xs text-slate-500">Aparece na coluna direita do rotulo, abaixo do peso. Suporta negrito, italico e sublinhado. Ideal para alegacoes de saude, campanhas ou info especial.</p>
          <RichTextEditor
            value={form.nota_extra || ''}
            onChange={v => set('nota_extra', v)}
            placeholder="ex: <b>Com certificado IFOS</b> - qualidade auditada independentemente..."
          />
        </section>

        {/* QR informativo */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">QR informativo</h2>
          <p className="text-xs text-slate-500">
            Se preencher este link, o rótulo mostra um QR pequeno e elegante na coluna direita, acima da zona da etiqueta.
            O texto impresso será: “Quer saber mais? Visite-nos em elemensys.pt”.
          </p>
          <Field label="Link do QR informativo" hint="ex: https://elemensys.pt/produto/b12-methyl">
            <Input
              value={form.info_qr_url || ''}
              onChange={v => set('info_qr_url', v)}
              placeholder="https://elemensys.pt/..."
            />
          </Field>
        </section>

                {/* Info legal */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2">Informacao Legal</h2>
          <Field label="Empresa / Distribuidor"><Textarea rows={2} value={form.empresa_bilingue} onChange={v => set('empresa_bilingue', v)} /></Field>
          <Field label="Certificacao GMP"><Textarea rows={2} value={form.gmp_bilingue} onChange={v => set('gmp_bilingue', v)} /></Field>
        </section>

        <div className="flex justify-end pt-4 border-t border-slate-800">
          <Button onClick={handleSave} disabled={saving} size="lg" className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {isEdit ? 'Guardar Alteracoes' : 'Criar Produto'}
          </Button>
        </div>
      </div>
    </div>
  );
}
